"use client";

import { SlidersHorizontal, ArrowUpDown, LayoutGrid, X } from "lucide-react";
import { BroomIcon } from "@phosphor-icons/react";
import { useCallback, useEffect, useState } from "react";
import DateRangePicker from "@/components/search/DateRangePicker";
import { cn } from "@/lib/utils";
import { EdgeFadeScroller } from "@/components/ui/edge-fade-scroller";
import { getCategoryByCode, getCategoryName } from "@/config/categories";
import type { Locale } from "@/i18n/routing";

type LocaleKey = Locale;

/** 지도 위에 띄운 알약·칩의 그림자 — 지도 배경 위에서 떠 보이게 */
const FLOATING_SHADOW =
  "shadow-[0_1px_2px_rgba(20,18,12,0.10),0_4px_12px_rgba(20,18,12,0.10)]";
/** 칩 줄 끝에 씌우는 페이드 폭 */
const CHIP_FADE_PX = 16;

/**
 * 스크롤 위치에 따른 마스크 — **가려진 쪽만** 흐린다.
 * 맨 왼쪽에서는 왼쪽을 흐리지 않는다(첫 칩이 반쯤 지워져 보이던 버그).
 */
function chipFadeStyle(
  fadeLeft: boolean,
  fadeRight: boolean,
): React.CSSProperties | undefined {
  if (!fadeLeft && !fadeRight) return undefined;
  const from = fadeLeft ? `transparent, black ${CHIP_FADE_PX}px` : "black";
  const to = fadeRight
    ? `black calc(100% - ${CHIP_FADE_PX}px), transparent`
    : "black";
  const mask = `linear-gradient(to right, ${from}, ${to})`;
  return { maskImage: mask, WebkitMaskImage: mask };
}

/**
 * 칩 줄의 가로 스크롤 상태를 관찰해 마스크 스타일을 돌려준다.
 * - 넘치지 않으면 마스크 없음
 * - `scrollLeft > 0` 이면 왼쪽, 끝에 닿지 않았으면 오른쪽만 흐림
 *
 * setState 는 rAF · ResizeObserver · scroll 콜백에서만 일어난다(effect 본문
 * setState 금지 규칙). ref 가 아니라 **콜백 ref + state** 라 렌더 중 ref 를 읽지 않는다.
 */
function useChipOverflow(
  enabled: boolean,
): [(node: HTMLDivElement | null) => void, React.CSSProperties | undefined] {
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const [edges, setEdges] = useState({ left: false, right: false });
  const attach = useCallback((el: HTMLDivElement | null) => setNode(el), []);

  useEffect(() => {
    if (!enabled || !node) return;

    const measure = () => {
      const left = node.scrollLeft > 0;
      const right = node.scrollLeft + node.clientWidth < node.scrollWidth - 1;
      // 같은 상태면 새 객체를 만들지 않는다 — 스크롤마다 리렌더하지 않도록.
      setEdges((prev) =>
        prev.left === left && prev.right === right ? prev : { left, right },
      );
    };

    const raf = requestAnimationFrame(measure);
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    node.addEventListener("scroll", measure, { passive: true });

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
      node.removeEventListener("scroll", measure);
    };
  }, [enabled, node]);

  return [attach, chipFadeStyle(edges.left, edges.right)];
}

/** 알약(날짜·필터·정렬·초기화)은 테두리 대신 링 + 그림자 */
const FLOATING_PILL = `ring-1 ring-black/10 ${FLOATING_SHADOW}`;
/** 활성 점 — 34px 알약의 우상단 곡선(반지름 17) 위, 오른쪽에서 10px 지점의 외곽선에
 *  중심을 두어 반은 알약 안, 반은 배경에 걸친다. 8px, 테두리 없음. */
const ACTIVE_DOT =
  "absolute top-0 right-0.5 h-2 w-2 rounded-full bg-orange-500 pointer-events-none";

interface SearchFilterBarProps {
  locale: string;
  checkinDate: string | null;
  checkoutDate: string | null;
  onDateChange: (checkin: string | null, checkout: string | null) => void;
  activeFilterCount: number;
  onFilterDialogOpen: () => void;
  isSortActive: boolean;
  onSortDialogOpen: () => void;
  /** 있으면 세 번째 알약이 정렬 대신 "카테고리"가 된다(검색 페이지). 정렬은 목록 헤더로. */
  onCategoryDialogOpen?: () => void;
  hideMap: boolean;
  /** drawer 내부에 배치할 때 true — fixed/sticky 대신 일반 flow */
  inDrawer?: boolean;
  /** 지도 위에 띄울 때 — 배경 없이 알약·칩만, 테두리 대신 링+그림자 */
  floating?: boolean;
  /** 활성 카테고리 칩 표시용 (선택된 카테고리 코드들) */
  selectedCategories?: string[];
  /** 칩의 ✕ 클릭 시 해당 카테고리 해제 */
  onRemoveCategory?: (code: string) => void;
  /** 전체 해제 */
  onClearCategories?: () => void;
  /**
   * 활성 카테고리 칩의 표현.
   * "solid"(기본) = 검정 채움(기존 상품 모드 그대로) · "outline" = 흰 바탕 + 진한 테두리.
   * 지점 허브처럼 칩이 여러 줄 겹쳐 놓이는 화면에서 면이 과해 보이지 않게 쓴다.
   */
  chipVariant?: "solid" | "outline";
  /**
   * 활성 카테고리 칩 **뒤에** 같은 줄로 이어 붙일 노드(구분선 + 추가 칩 등).
   * 지점 허브가 클래스 칩을 카테고리 칩과 한 줄에 놓기 위해 쓴다 —
   * 칩 줄을 하나 더 쌓지 않으려는 목적이라 기본값은 없음(상품 모드 무변경).
   */
  chipsSuffix?: React.ReactNode;
  /**
   * 줄 **맨 앞**에 놓을 노드 — 지점 허브의 [지점 | 차종] · [캠핑장 | 장비] 토글(10-13).
   * 한 줄 모드(데스크톱)는 [필터] 앞, 그 밖(지도 위·드로어·태블릿)은 알약 줄의 맨 앞.
   */
  leading?: React.ReactNode;
  /**
   * 활성 카테고리 칩과 초기화 버튼을 **그리지 않는다**(chipsSuffix 만 남긴다).
   * 지점 허브처럼 카테고리가 화면 자체의 전제(오토바이·스쿠터)라 칩이 정보를 주지
   * 않고 클래스 칩 자리를 뺏을 때 쓴다. 카테고리는 필터 드로어에서 계속 바꿀 수
   * 있고 필터 버튼의 배지 숫자에 포함된다.
   */
  hideCategoryChips?: boolean;
  /** 정렬 알약을 숨긴다 — 목록 안에 정렬이 따로 있는 화면(허브). */
  hideSort?: boolean;
  /**
   * 칩 줄이 넘칠 때 가려진 쪽 끝을 흐린다(기본 꺼짐 — 상품 모드 무변경).
   * 지도 위(floating)는 glass(내용만 투명하게), 흰 바탕 줄은 흰 블러 오버레이.
   */
  chipsFade?: boolean;
  /**
   * 모바일 좌우 여백 클래스(기본 `px-4`). 지점 허브는 지도 위 레이어(12px)와
   * 드로어 안이 **같은 선**을 쓰도록 `px-3` 을 넘긴다.
   */
  mobileGutter?: string;
  /**
   * 데스크톱 가로 한 줄(09-23, 에어비앤비 방식) — 날짜·필터·카테고리를 글자 폭 알약으로
   * 줄이고, 칩(과 chipsSuffix)을 **같은 줄** 오른쪽에 잇는다. 목록·지도 위를 가로지르는
   * 필터 줄에서 쓴다. 기본(false)은 지금처럼 버튼 꽉 채우기 + 아래 칩 줄.
   */
  inline?: boolean;
  /**
   * [필터] 바로 뒤·[카테고리] 앞에 놓을 노드(09-28 지역·명소 알약). "어디 근처"가
   * 무엇을 고르는지보다 먼저 온다.
   */
  afterFilter?: React.ReactNode;
  /**
   * [필터] 버튼을 그리지 않는다 — 모바일 지도 위 줄. 필터는 상단 검색바 오른쪽 버튼으로
   * 옮겼다(09-28, filter-bridge).
   */
  hideFilterButton?: boolean;
  /**
   * 모바일(< 640px)에서 [날짜]·[필터] 를 숨긴다(09-28) — 모바일은 날짜를 상단 두 줄 검색바에서,
   * 필터를 그 오른쪽 버튼에서 고른다. 태블릿 이상은 그대로 보인다.
   */
  mobileMinimal?: boolean;
  t: (key: string, params?: Record<string, string | number>) => string;
}

export default function SearchFilterBar({
  locale,
  checkinDate,
  checkoutDate,
  onDateChange,
  activeFilterCount,
  onFilterDialogOpen,
  isSortActive,
  onSortDialogOpen,
  onCategoryDialogOpen,
  hideMap,
  inDrawer = false,
  floating = false,
  selectedCategories = [],
  onRemoveCategory,
  onClearCategories,
  chipVariant = "solid",
  chipsSuffix,
  hideCategoryChips = false,
  hideSort = false,
  chipsFade = false,
  leading,
  mobileGutter = "px-4",
  inline = false,
  afterFilter,
  hideFilterButton = false,
  mobileMinimal = false,
  t,
}: SearchFilterBarProps) {
  const [attachChipScroll, chipFade] = useChipOverflow(chipsFade);
  const positionClass = floating
    ? // 래퍼가 지도 드래그를 먹지 않도록 — 알약·칩만 pointer-events-auto
      "pointer-events-none"
    : inDrawer
    ? "bg-white"
    : hideMap
    ? // 지도 없는 목록(택배·상품 검색) — 헤더 아래 가로 줄, 아래 경계선(09-28 지도 화면 필터 줄과 같은 모양)
      "z-40 sticky top-[calc(4.5rem+var(--loc-safe-area-inset-top))] md:top-[calc(81px+var(--loc-safe-area-inset-top))] bg-white border-b border-gray-200 sm:py-3"
    : "bg-white";

  const chipNodes = (hideCategoryChips ? [] : selectedCategories).map(
    (code) => {
      const category = getCategoryByCode(code);
      const name = category
        ? getCategoryName(category, locale as LocaleKey)
        : code;
      return (
        <button
          key={code}
          type="button"
          onClick={() => onRemoveCategory?.(code)}
          className={cn(
            "shrink-0 inline-flex items-center gap-1 pl-3 pr-2 py-1.5 rounded-full text-sm font-medium transition",
            chipVariant === "outline"
              ? floating
                ? `pointer-events-auto border-2 border-gray-900 bg-white text-gray-900 ${FLOATING_SHADOW}`
                : "border-2 border-gray-900 bg-white text-gray-900 hover:bg-gray-50"
              : floating
              ? `pointer-events-auto border border-orange-300 bg-orange-50 text-orange-600 ${FLOATING_SHADOW}`
              : "border border-gray-900 bg-gray-900 text-white hover:bg-gray-800",
          )}
        >
          <span className="whitespace-nowrap">{name}</span>
          <X className="w-3.5 h-3.5" />
        </button>
      );
    },
  );

  const resetChip =
    !hideCategoryChips && selectedCategories.length >= 2 ? (
      floating ? (
        // 지도 위: 가로 폭이 귀해 글자 대신 빗자루 아이콘 원(칩과 같은 32px 높이).
        // 칩의 ✕(하나 지우기)와 모양이 달라 "전부 지우기"로 읽힌다.
        <button
          type="button"
          onClick={onClearCategories}
          aria-label={t("reset")}
          title={t("reset")}
          className={`pointer-events-auto shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full bg-white text-gray-600 ${FLOATING_PILL}`}
        >
          <BroomIcon className="w-4 h-4" weight="regular" />
        </button>
      ) : (
        <button
          type="button"
          onClick={onClearCategories}
          className="shrink-0 inline-flex items-center px-3 py-1.5 rounded-full bg-white text-gray-500 text-sm font-medium whitespace-nowrap border border-gray-200 hover:bg-gray-50 transition"
        >
          {t("reset")}
        </button>
      )
    ) : null;

  // 한 줄 모드: 버튼은 글자 폭(좌우 16px), 기본: 줄을 나눠 꽉 채우기.
  const buttonWidth = inline ? "shrink-0 px-4" : "flex-1";
  // 실제로 그릴 칩이 있을 때만 줄을 잇는다 — 카테고리 칩을 숨긴 화면에서 카테고리만
  // 골라 두면 빈 칩 줄(예전엔 빈 구분선)만 남던 문제(09-28).
  const inlineChips =
    inline && ((!hideCategoryChips && selectedCategories.length > 0) || !!chipsSuffix);

  return (
    <div className={positionClass}>
      <div
        className={cn(
          floating
            ? "flex items-center gap-1"
            : `${mobileGutter} py-2 sm:px-0 sm:py-0 mx-auto flex items-center gap-1`,
          inline && "gap-2",
          hideMap && !inDrawer && "max-w-7xl sm:px-8",
        )}
      >
        {/* 날짜 버튼 — 넓게. 가로 한 줄(데스크톱)에서는 날짜가 검색바(위치 | 날짜 | 무엇을)로
            올라가 있어 빼고 [필터] | [지역·명소]만 남긴다(09-23 검색바 개편). */}
        {!inline && (
          <div className={cn("flex-1", mobileMinimal && "max-sm:hidden")}>
            <DateRangePicker
              checkinDate={checkinDate}
              checkoutDate={checkoutDate}
              onDateChange={onDateChange}
              inDrawer={inDrawer}
              floating={floating}
              variant="bar"
            />
          </div>
        )}

        {inline && leading ? <div className="shrink-0">{leading}</div> : null}
        <div className={inline ? "flex shrink-0 gap-2" : "flex-1 flex gap-1"}>
          {/* 필터 버튼 — flex-1 */}
          {!hideFilterButton && (
          <button
            onClick={onFilterDialogOpen}
            className={cn(
              "relative flex h-8.5 items-center justify-center gap-1 rounded-full bg-white text-[13px] text-gray-700 transition-all duration-150 hover:bg-gray-50",
              // 데스크톱(지도 폭)에서는 한 단계 크게 — 목록 카드와 무게를 맞춘다(09-23)
              "mapShow:h-10 mapShow:text-sm",
              buttonWidth,
              mobileMinimal && "max-sm:hidden",
              activeFilterCount > 0 ? "font-semibold" : "font-medium",
              floating
                ? `pointer-events-auto ${FLOATING_PILL}`
                : "border border-gray-300",
            )}
          >
            {/* 활성 표시 — 알약 우상단 모서리 곡선 위에 걸친 주황 점(반은 알약, 반은 배경) */}
            {activeFilterCount > 0 && <span className={ACTIVE_DOT} />}
            <SlidersHorizontal className="w-3.5 h-3.5" />
            <span>
              {activeFilterCount > 0
                ? `${t("filterBar.filter")} ${activeFilterCount}`
                : t("filterBar.filter")}
            </span>
          </button>
          )}

          {afterFilter}

          {/* 카테고리 버튼 — onCategoryDialogOpen 이 있으면 정렬 알약 자리를 대신한다 */}
          {onCategoryDialogOpen ? (
            <button
              onClick={onCategoryDialogOpen}
              className={cn(
                "relative flex h-8.5 items-center justify-center gap-1 rounded-full bg-white text-[13px] text-gray-700 transition-all duration-150 hover:bg-gray-50",
              // 데스크톱(지도 폭)에서는 한 단계 크게 — 목록 카드와 무게를 맞춘다(09-23)
              "mapShow:h-10 mapShow:text-sm",
              buttonWidth,
                selectedCategories.length > 0 ? "font-semibold" : "font-medium",
                floating
                  ? `pointer-events-auto ${FLOATING_PILL}`
                  : "border border-gray-300",
              )}
            >
              {selectedCategories.length > 0 && <span className={ACTIVE_DOT} />}
              <LayoutGrid className="w-3.5 h-3.5" />
              <span>
                {selectedCategories.length > 0
                  ? `${t("filterBar.category")} ${selectedCategories.length}`
                  : t("filterBar.category")}
              </span>
            </button>
          ) : /* 정렬 버튼 — flex-1 (허브는 목록 안 정렬이 있어 숨긴다) */
          hideSort ? null : (
            <button
              onClick={onSortDialogOpen}
              className={cn(
                "relative flex h-8.5 items-center justify-center gap-1 rounded-full bg-white text-[13px] font-medium text-gray-700 transition-all duration-150 hover:bg-gray-50",
                "mapShow:h-10 mapShow:text-sm",
                buttonWidth,
                floating
                  ? `pointer-events-auto ${FLOATING_PILL}`
                  : "border border-gray-300",
              )}
            >
              {isSortActive && (
                <span className="absolute top-0.5 right-0.5 w-2.5 h-2.5 bg-orange-500 rounded-full border-2 border-white" />
              )}
              <ArrowUpDown className="w-3.5 h-3.5" />
              <span>{t("filterBar.sort")}</span>
            </button>
          )}
        </div>

        {/* 한 줄 모드: [필터] 바로 뒤에 칩을 같은 줄로(넘치면 가로 스크롤). 구분선은 두지 않는다
            — 일반 검색의 [필터] [명소] 줄과 같게(10-13). 세로 여백은 overflow 가 잘라내지 않게
            py-2 -my-2 (아래 칩 줄과 같은 처리). */}
        {inlineChips ? (
          <>
            <div
              ref={attachChipScroll}
              style={chipFade}
              className="-my-2 flex min-w-0 flex-1 items-center gap-2 overflow-x-auto py-2 scrollbar-hide"
            >
              {chipNodes}
              {chipsSuffix}
              {resetChip}
            </div>
          </>
        ) : null}
      </div>

      {/* 활성 카테고리 칩 — 적용된 필터를 눈에 보이게(✕로 해제) */}
      {!inline &&
        ((!hideCategoryChips && selectedCategories.length > 0) || chipsSuffix || leading) &&
        (floating ? (
          // 지도 위: 초기화 칩은 맨 앞에 고정, 카테고리 칩만 가로 스크롤.
          <div className="pt-2 flex items-center gap-1.5">
            {resetChip}
            {/* 전폭 줄(10-01) — 호출부는 지도 위 레이어를 left-3 right-3(12px)로 띄운다
                (검색 페이지·지점 허브 공통). 래퍼가 그 거터를 -mx-3 으로 상쇄해 화면 끝에서
                끝까지 스크롤되고, 거터는 안쪽 줄 px-3 이 맡는다: 첫 알약은 12px 에서 시작,
                끝까지 넘기면 마지막 알약도 12px 를 남기고 멈춘다. 초기화 칩이 앞에 고정된
                경우엔 왼쪽은 그 칩 뒤에서 시작한다(왼쪽만 상쇄 안 함).
                바탕이 지도라 흰 띠 대신 glass(내용만 가장자리에서 투명하게)로 흐린다. */}
            {/* overflow-x-auto 는 세로도 잘라내 칩의 링·그림자가 아래·옆에서 끊긴다.
                그림자 번짐(4px 오프셋 + 12px 블러)만큼 안쪽 여백을 주고 같은 값의
                음수 마진으로 자리를 되돌려 눈에 보이는 위치는 그대로 둔다. */}
            {/* 스크롤 컨테이너 자체가 포인터를 받아야 한다 — 바깥 래퍼는
                pointer-events-none(지도 드래그 통과)이라, 칩만 auto 로 두면 칩 사이
                틈에서 시작한 드래그가 지도로 가고 iOS WebView(Capacitor)는 칩 위
                드래그도 지도 제스처로 뺏어 가로 스크롤이 끊긴다. touch-action:
                pan-x 로 이 줄에서는 가로 팬만 브라우저 스크롤로 처리한다. */}
            <EdgeFadeScroller
              fade={chipsFade ? "glass" : "none"}
              wrapperClassName={cn("min-w-0 flex-1 -mr-3", resetChip ? "-ml-1" : "-ml-3")}
              className="pointer-events-auto touch-pan-x overscroll-x-contain py-4 -my-4"
            >
              <div
                className={cn(
                  "flex w-max items-center gap-1.5 pr-3",
                  resetChip ? "pl-1" : "pl-3",
                )}
              >
                {leading}
                {chipNodes}
                {chipsSuffix}
              </div>
            </EdgeFadeScroller>
          </div>
        ) : (
          <div
            className={cn(
              // 모바일: 버튼 줄 아래 8px(래퍼 py-2)+4px, 칩 아래 8px — 지도와 붙지 않게.
              // sm 이상은 바깥 래퍼가 세로 여백을 주므로 위 8px만 두고 아래는 0 (b296d4b6 의도 유지).
              // 모바일 거터는 래퍼가 아니라 안쪽 줄이 갖는다 — 줄이 화면 끝에서 끝까지
              // 스크롤되고 첫·마지막 칩만 거터만큼 안에 선다(10-01 전폭 줄).
              "pt-1 pb-2 sm:pt-2 sm:pb-0",
              hideMap && !inDrawer && "max-w-7xl mx-auto sm:px-8",
            )}
          >
            {/* 여백은 바깥 래퍼가, 스크롤은 이 줄이 맡는다 — overflow-x-auto 는 세로도
                잘라내서 칩의 테두리·그림자가 아래에서 끊긴다(데스크톱 목록 열은 아래
                여백이 0이라 특히 눈에 띈다, 09-23). 지도 위 줄과 같은 처리: 안쪽 여백
                만큼 음수 마진으로 되돌려 보이는 위치는 그대로 둔다. */}
            <EdgeFadeScroller
              fade={chipsFade ? "white" : "none"}
              className="py-2 -my-2"
            >
              <div
                className={cn("flex w-max items-center gap-1.5", mobileGutter, "sm:px-0")}
              >
                {leading}
                {chipNodes}
                {chipsSuffix}
                {resetChip}
              </div>
            </EdgeFadeScroller>
          </div>
        ))}
    </div>
  );
}
